import React, {
  useEffect,
  FC,
  useMemo,
} from 'react';
import noop from 'lodash/noop';
import Dialog from 'pages/Dashboard/components/Dialog';
import keyBy from 'lodash/keyBy';
import {
  Props as FieldsArrayWrapperProps,
} from 'pages/Dashboard/pages/Charts/pages/Details/components/FieldsArrayWrapper';
import AllergyInfoItem from 'pages/Dashboard/pages/Charts/pages/Details/components/GenericSection/AllergyInfoItem';
import {
  FormProvider,
  useForm,
} from 'react-hook-form';
import SurgeryInfoItem from 'pages/Dashboard/pages/Charts/pages/Details/components/GenericSection/SurgeryInfoItem';
import MedicalHistoryInfoItem from 'pages/Dashboard/pages/Charts/pages/Details/components/GenericSection/MedicalHistoryInfoItem';
import FamilyMedicalInfoItem from 'pages/Dashboard/pages/Charts/pages/Details/components/GenericSection/FamilyInfoItem';
import {
  PatientMedicalBackgroundResponseDTO,
} from 'dtos/patientMedicalBackgroundResponseDTO';
import {
  useGetMedicalBackground,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/hooks';
import isNil from 'lodash/isNil';
import Button from 'pages/Dashboard/components/Button';
import {
  getSubmitButtonText,
} from '@populateui/utils';
import {
  useUpdatePatientChart,
} from 'pages/Dashboard/pages/Charts/pages/Details/hooks';
import {
  normalizeDemographicPayload,
} from 'pages/Dashboard/pages/Charts/pages/Details/utils/helper';
import {
  useNotify,
} from 'core/hooks';
import Skeleton from '@mui/material/Skeleton';
import {
  Patient,
  PatientForm,
  ReviewOfSystemElement,
} from 'pages/Dashboard/types/patient';
import SocialHistories from 'pages/Dashboard/pages/Charts/pages/Details/components/SocialHistories';
import {
  Props as TabProps,
} from 'pages/Dashboard/pages/Charts/pages/Details/types/tabContentProps';
import ReviewOfSystems, {
  ReviewOfSystemsProps,
} from 'pages/Dashboard/pages/Charts/pages/Details/components/ReviewOfSystems';
import BMIInputs from 'views/EMR/components/MedicalBackgroundDialog/BMIInputs';
import pick from 'lodash/pick';
import ERxDialog from 'views/EMR/components/MedicalBackgroundDialog/ErxDialog';
import {
  patientEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import SortableFieldsArrayWrapper from 'pages/Dashboard/pages/Charts/pages/Details/components/SortableFieldsArrayWrapper';

export type MedicalBackgroundParams = {
  patient: Patient;
  id: string;
};

type Props = MedicalBackgroundParams & {
  onClose: () => void;
};

type CustomComponentProps = Pick<TabProps, 'patient'> & ReviewOfSystemsProps;

export type PatientMedicalBackgroundForm = Omit<PatientMedicalBackgroundResponseDTO, 'reviewOfSystem'> & {
  groupedReviewOfSystemElements?: Record<string, ReviewOfSystemElement[]>;
  feet?: number;
  inches?: number;
  age?: number | null;
};

type SectionData = Partial<FieldsArrayWrapperProps> & {
  id: string;
  title: string;
  CustomComponent?: FC<CustomComponentProps>;
  keys?: (keyof PatientMedicalBackgroundForm)[];
};

export const patientMedicalBackgroundSections: SectionData[] = [
  { title: 'Medications', id: 'medications' },
  { title: 'Drug Allergies', id: 'drugAllergies' },
  { title: 'Non-Drug Allergies', id: 'nonDrugAllergies', FieldComponent: AllergyInfoItem },
  { title: 'Medical History', id: 'medicalHistories', FieldComponent: MedicalHistoryInfoItem },
  { title: 'Surgical History', id: 'surgicalHistories', FieldComponent: SurgeryInfoItem },
  { title: 'Social History', id: 'socialHistory', CustomComponent: SocialHistories },
  {
    title: 'Family History',
    id: 'familyHistories',
    FieldComponent: FamilyMedicalInfoItem,
    getDefaultValue: () => ({
      diseaseName: '',
      familyRelation: 'Parent',
    }),
  },
  { title: 'BMI', id: 'bmi', CustomComponent: BMIInputs, keys: ['height', 'weight', 'feet', 'inches'] },
  {
    title: 'Review of Systems',
    id: 'groupedReviewOfSystemElements',
    CustomComponent: ReviewOfSystems,
    keys: ['groupedReviewOfSystemElements'],
  },
];

export const patientMedicalBackgroundSectionsById = keyBy(patientMedicalBackgroundSections, 'id');

type FormComponentProps = SectionData & {
  patient: PatientForm;
};

function FormComponent({
  title,
  FieldComponent,
  CustomComponent,
  id,
  patient,
  getDefaultValue,
}: FormComponentProps) {
  const isCustomComponent = !isNil(CustomComponent);
  const Component = isCustomComponent ? CustomComponent : SortableFieldsArrayWrapper;

  return (
    <Component
      patient={patient}
      key={id}
      FieldComponent={FieldComponent!}
      formKey={id}
      append={{ text: `Add ${title}` }}
      classes={{ field: '!w-full', title: '!hidden', body: '!grid-cols-2' }}
      getDefaultValue={getDefaultValue}
    />
  );
}

export default function MedicalBackgroundDialog({
  onClose,
  patient,
  id,
}: Props) {
  const notify = useNotify();
  const section = patientMedicalBackgroundSectionsById[id];

  const { title, keys } = section;
  const isERx = /^(medications|drugallergies)$/i.test(id);

  const {
    data,
    isFetching,
  } = useGetMedicalBackground(patient?.patientId ?? 0, patient?.dateOfBirth ?? '', !isERx);

  const sectionValues = useMemo(() => pick(data ?? {}, keys ?? [id]), [data]);

  const {
    mutateAsync: updatePatientChart,
  } = useUpdatePatientChart();

  const formProps = useForm<PatientMedicalBackgroundForm>({
    mode: 'onChange',
    defaultValues: { ...sectionValues, age: patient?.age },
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = formProps;

  useEffect(() => {
    if (!isNil(sectionValues)) {
      reset?.({ ...sectionValues, age: patient?.age }, {
        keepDirty: false,
        keepTouched: false,
      });
    }
  }, [sectionValues]);

  const onUpdateInfo = () => {
    onClose?.();
    patientEventChannel.emit('onMedicalBackgroundChange', patient?.patientId ?? 0, id);
  };

  const onSubmit = async ({ age, ...formData }: PatientMedicalBackgroundForm) => {
    try {
      await updatePatientChart({
        ...normalizeDemographicPayload({
          ...formData,
          dateOfBirth: patient?.dateOfBirth ?? '',
        }),
        patientId: patient?.patientId ?? 0,
        // Hack to prevent clearing middle name as sending null means remove middle name.
        middleName: patient.middleName,
      });

      notify({
        variant: 'success',
        message: `${title} saved successfully!`,
      });

      onUpdateInfo?.();
    } catch (e: unknown) {
      console.error(e);
    }
  };

  const dialogTitle = `Edit Patient's ${title}`;

  return isERx
    ? (
      <ERxDialog
        title={dialogTitle}
        onClose={onUpdateInfo}
        patient={patient as PatientForm}
      />
    )
    : (
      <Dialog
        open
        title={dialogTitle}
        onClose={onClose}
        onConfirm={noop}
        className={id === 'bmi' ? '!w-100' : '!w-160'}
      >
        <FormProvider {...formProps}>
          <div className="flex flex-1 flex-col gap-form-row populate-form min-h-[10rem] w-full justify-between">
            <div className="h-full max-h-[70vh] overflow-auto">
              {isFetching
                ? (
                  <div className="flex flex-col gap-2">
                    <Skeleton variant="rectangular" height={30} />
                    <Skeleton variant="rectangular" height={30} />
                  </div>
                )
                : (<FormComponent {...section} patient={patient as PatientForm} />)}
            </div>
            <div className="flex flex-row justify-end items-center gap-2">
              <Button
                variant="outlined"
                onClick={onClose}
              >
                Close
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={isFetching || isSubmitting}
                onClick={handleSubmit(onSubmit)}
              >
                {getSubmitButtonText(false, isSubmitting)}
              </Button>
            </div>
          </div>
        </FormProvider>
      </Dialog>
    );
}
